import React, { useState, useEffect, useRef } from "react";
import { EvaluationSection } from "../../../_services/DvsEvaluationService";
import "../dvs-evaluation.css";

interface Props {
  sections: EvaluationSection[];
}

// Declare MathJax typings
declare global {
  interface Window {
    MathJax?: any;
  }
}

// Convert <script type="math/tex"> tags to MathJax delimiters
function convertMathScriptTags(html: string): string {
  if (!html) return html;

  // Replace <script type="math/tex">...</script> with \(...\) for inline math
  let converted = html.replace(
    /<script\s+type=["']math\/tex["']>(.*?)<\/script>/gi,
    (match, latex) => `\\(${latex}\\)`
  );

  // Replace <script type="math/tex; mode=display">...</script> with \[...\] for display math
  converted = converted.replace(
    /<script\s+type=["']math\/tex;\s*mode=display["']>(.*?)<\/script>/gi,
    (match, latex) => `\\[${latex}\\]`
  );

  return converted;
}

const OnlinePaperView: React.FC<Props> = ({ sections }) => {
  const rootRef = useRef<HTMLDivElement>(null);

  // Typeset question statements / answer keys whenever sections change.
  // MathJax's initial page-load scan can miss content rendered later by React,
  // so we explicitly queue a Typeset on the root after each render.
  useEffect(() => {
    if (rootRef.current && window.MathJax?.Hub) {
      try {
        window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub, rootRef.current]);
      } catch (err) {
        console.warn("MathJax rendering failed:", err);
      }
    }
  }, [sections]);

  return (
    <div
      ref={rootRef}
      className="dvs-eval-root"
      style={{ height: "100%", overflowY: "auto", padding: "20px 24px" }}
    >
      {sections.map((section) =>
        section.showTotalMarksOnly ? null : (
          <div key={section.sectionId} style={{ marginBottom: "22px" }}>
            <div className="dvs-eval-section-header">{section.sectionName}</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }} className="dvs-eval-question-card">
              <tbody>
                {section.questions.map((q) => (
                  <QuestionRow key={q.attemptId} question={q} />
                ))}
              </tbody>
            </table>
          </div>
        ),
      )}
      {sections.length === 0 && (
        <div style={{ textAlign: "center", color: "var(--text-muted)", padding: "40px" }}>
          No online answers available.
        </div>
      )}
    </div>
  );
};

const QuestionRow: React.FC<{ question: any }> = ({ question: q }) => {
  const [activeTab, setActiveTab] = useState<"html" | "raw">("html");
  const htmlPanelRef = useRef<HTMLDivElement>(null);

  // Hide student response for MCQ-style questions (any non-text/essay category).
  // Text/essay questions have questionCategory === 0 or null.
  const isTextQuestion = q.questionCategory === 0 || q.questionCategory === null;

  // Convert math script tags to MathJax delimiters
  const convertedStudentResponse = convertMathScriptTags(q.studentResponse || "");
  const convertedQuestionStatement = convertMathScriptTags(q.questionStatement || "");
  const convertedAnswerKey = convertMathScriptTags(q.answerKey || "");

  // Trigger MathJax rendering when HTML tab is shown
  useEffect(() => {
    if (activeTab === "html" && htmlPanelRef.current && window.MathJax) {
      try {
        window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub, htmlPanelRef.current]);
      } catch (err) {
        console.warn("MathJax rendering failed:", err);
      }
    }
  }, [activeTab, convertedStudentResponse]);

  return (
    <>
      {/* Question statement */}
      <tr>
        <td>
          <span className="dvs-eval-qnum">{q.questionNumber}</span>
          {q.questionStatement ? (
            <span
              className="dvs-eval-question-text"
              dangerouslySetInnerHTML={{ __html: convertedQuestionStatement }}
            />
          ) : (
            <span className="dvs-eval-question-text" style={{ color: "var(--text-faint)" }}>
              Question {q.questionNumber}
            </span>
          )}
        </td>
      </tr>

      {/* Answer key — always shown for text questions (label acts as placeholder
          for the evaluator even when empty). For MCQ, only show if data exists. */}
      {(isTextQuestion || q.answerKey) && (
        <tr className="dvs-eval-answer-row">
          <td>
            <strong>Answer:</strong>{" "}
            {q.answerKey && <span dangerouslySetInnerHTML={{ __html: convertedAnswerKey }} />}
          </td>
        </tr>
      )}

      {/* Student response — text/essay questions only. HTML/Raw tabs always shown
          (matches Java production behavior); content area is empty when student
          didn't write anything. */}
      {isTextQuestion && (
        <tr className="dvs-eval-response-row">
          <td>
            <strong>Student Response:</strong>
            <div style={{ marginTop: "8px" }}>
              <div className="dvs-eval-tabs">
                <button
                  type="button"
                  onClick={() => setActiveTab("html")}
                  className={`dvs-eval-tab ${activeTab === "html" ? "dvs-eval-tab--active" : ""}`}
                >
                  HTML
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("raw")}
                  className={`dvs-eval-tab ${activeTab === "raw" ? "dvs-eval-tab--active" : ""}`}
                >
                  Raw
                </button>
              </div>
              {activeTab === "html" ? (
                <div
                  ref={htmlPanelRef}
                  className="dvs-eval-tab-panel"
                  dangerouslySetInnerHTML={{ __html: convertedStudentResponse }}
                />
              ) : (
                <textarea
                  disabled
                  value={q.studentResponse || ""}
                  className="dvs-eval-tab-panel dvs-eval-tab-panel--raw"
                  style={{ height: "200px" }}
                />
              )}
            </div>
          </td>
        </tr>
      )}

      {/* Attachments */}
      {q.attachments && q.attachments.length > 0 && (
        <tr>
          <td style={{ padding: "8px 8px 8px 30px", borderBottom: "1px solid var(--border-default)" }}>
            {q.attachments.map((att: any, idx: number) => (
              <span
                key={idx}
                style={{
                  display: "inline-block",
                  background: "var(--surface-sunken)",
                  padding: "3px 8px",
                  borderRadius: "3px",
                  marginRight: "6px",
                  marginBottom: "4px",
                  fontSize: "0.85em",
                }}
              >
                <a href={att.url} target="_blank" rel="noreferrer">
                  {att.name || `Attachment ${idx + 1}`}
                </a>
              </span>
            ))}
          </td>
        </tr>
      )}
    </>
  );
};

export default OnlinePaperView;
